// Pull-to-refresh for the SPA's main scroll area, native-app feel.
//
// When the user drags down while already scrolled to the top, we reveal a
// spinner and, past a threshold, refetch the app's data (React Query cache
// invalidation, no full page reload, so it stays snappy). Touch-driven, so
// it's a no-op with a mouse on desktop.
import {
  useRef,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { shouldReleasePull } from "../lib/pull-gesture";

const THRESHOLD = 64; // px of pull (after resistance) needed to trigger
const MAX_PULL = 96; // clamp the visual pull
const RESISTANCE = 0.5; // drag feels weighty
// px of deliberate downward pull before we ENGAGE (take over the gesture). The
// first few px are left un-prevented on purpose: a non-passive `preventDefault`
// on an *ambiguous* move drops Android's WebView off its fast single-finger
// scroll path, so single-finger scrolling stops working and the user needs two
// fingers (Ben's report). Not touching the gesture until it's a clear pull keeps
// normal scrolling native on Android; iOS behaves identically, just armed 6px in.
const ENGAGE = 6;

export function PullToRefresh({ children }: { children: ReactNode }) {
  const scrollRef = useRef<HTMLElement>(null);
  const startY = useRef<number | null>(null);
  const engaged = useRef(false);
  const pullRef = useRef(0);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const qc = useQueryClient();

  const runRefresh = useCallback(async () => {
    setRefreshing(true);
    setPull(THRESHOLD);
    pullRef.current = THRESHOLD;
    try {
      await qc.invalidateQueries();
      // Keep the spinner visible briefly so the refresh reads as deliberate.
      await new Promise((r) => setTimeout(r, 450));
    } finally {
      setRefreshing(false);
      setPull(0);
      pullRef.current = 0;
    }
  }, [qc]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onStart = (e: TouchEvent) => {
      engaged.current = false;
      // Single-finger only + genuinely at the top → a candidate pull. Multi-touch
      // (pinch/two-finger) is never a pull and must never be intercepted.
      if (refreshing || e.touches.length !== 1) { startY.current = null; return; }
      startY.current = el.scrollTop <= 0 ? e.touches[0].clientY : null;
    };
    const onMove = (e: TouchEvent) => {
      if (startY.current == null || refreshing || e.touches.length !== 1) return;
      const dy = e.touches[0].clientY - startY.current;
      // Release the moment we're no longer pulling down at the very top, and
      // null the start so this gesture can't re-latch if the finger reverses
      // again. Without the `scrollTop > 0` re-check the page can't scroll back
      // up after scrolling down within one drag.
      if (shouldReleasePull(dy, el.scrollTop)) {
        if (pullRef.current !== 0) {
          pullRef.current = 0;
          setPull(0);
        }
        startY.current = null;
        engaged.current = false;
        return;
      }
      // Don't take over until the pull is DELIBERATE. Leaving the first few px
      // un-prevented keeps Android's single-finger scroll on the native fast
      // path, a non-passive preventDefault on ambiguous moves is exactly what
      // forced two-finger scrolling. Once engaged we own the gesture (identical
      // on iOS, which never had the issue).
      if (!engaged.current) {
        if (dy < ENGAGE) return;
        engaged.current = true;
      }
      if (e.cancelable) e.preventDefault();
      const p = Math.min(MAX_PULL, dy * RESISTANCE);
      pullRef.current = p;
      setPull(p);
    };
    const onEnd = () => {
      engaged.current = false;
      if (startY.current == null) return;
      startY.current = null;
      if (pullRef.current >= THRESHOLD && !refreshing) {
        void runRefresh();
      } else {
        pullRef.current = 0;
        setPull(0);
      }
    };

    // touchmove must be non-passive so preventDefault works.
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd, { passive: true });
    el.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [refreshing, runRefresh]);

  // Snap back smoothly when not actively dragging.
  const settling = startY.current == null;
  const indicatorOpacity = Math.min(1, pull / THRESHOLD);

  return (
    <main ref={scrollRef} className="venue-main">
      <div
        className="ptr-indicator"
        style={{
          transform: `translateY(${pull - 44}px)`,
          opacity: indicatorOpacity,
          transition: settling ? "transform 0.2s ease, opacity 0.2s ease" : "none",
        }}
        aria-hidden
      >
        <span className={`ptr-spinner${refreshing ? " spinning" : ""}`}>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        </span>
      </div>
      <div
        className="ptr-content"
        style={{
          transform: pull ? `translateY(${pull}px)` : undefined,
          transition: settling ? "transform 0.2s ease" : "none",
        }}
      >
        {children}
      </div>
    </main>
  );
}
